import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoPrivateCommentRepliesResult,
  PostVideoPrivateCommentRepliesOptions,
  GetVideoPrivateCommentRepliesOptions,
  GetVideoPrivateCommentRepliesResult,
  getVideoPrivateCommentReplies,
  PostVideoPrivateCommentRepliesFields,
  postVideoPrivateCommentReplies,
  GetVideoPrivateCommentRepliesFields,
} from '../fetchers/video/private_comment/replies';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoPrivateCommentRepliesOptions<Fields extends GetVideoPrivateCommentRepliesFields> =
  GetVideoPrivateCommentRepliesOptions<Fields>;

/**
 * Get all the replies to a private comment
 * This method returns every reply for the specified private comment.
 */

export function useGetVideoPrivateCommentReplies<
  Fields extends GetVideoPrivateCommentRepliesFields,
>(
  getOptions:
    | UseGetVideoPrivateCommentRepliesOptions<Fields>
    | (() => UseGetVideoPrivateCommentRepliesOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoPrivateCommentRepliesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/private_comments/${
          options.where.commentId
        }/replies${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoPrivateCommentReplies({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateCommentReplies, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies',
    method: 'GET',
  });
}

/**
 * Get all the replies to a private comment
 * This method returns every reply for the specified private comment.
 */

export function useGetVideoPrivateCommentRepliesLazy<
  Fields extends GetVideoPrivateCommentRepliesFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetVideoPrivateCommentRepliesResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetVideoPrivateCommentRepliesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${
            options.where.commentId
          }/replies${serializeQuery(options)}`,
          getVideoPrivateCommentReplies({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateCommentRepliesLazy, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies',
    method: 'GET',
  });
}

type UseGetVideoPrivateCommentRepliesOptionsInfinite<
  Fields extends GetVideoPrivateCommentRepliesFields,
> = GetVideoPrivateCommentRepliesOptions<Fields>;

/**
 * Get all the replies to a private comment
 * This method returns every reply for the specified private comment.
 */

export function useGetVideoPrivateCommentRepliesInfinite<
  Fields extends GetVideoPrivateCommentRepliesFields,
>(
  getOptions:
    | UseGetVideoPrivateCommentRepliesOptionsInfinite<Fields>
    | (() => UseGetVideoPrivateCommentRepliesOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoPrivateCommentRepliesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (
    pageIndex: number,
    previousPageData: GetVideoPrivateCommentRepliesResult<Fields> | null,
  ) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/private_comments/${options.where.commentId}/replies?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoPrivateCommentReplies({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoPrivateCommentRepliesInfinite, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies',
    method: 'GET',
  });
}

type UsePostVideoPrivateCommentRepliesOptions<Fields extends PostVideoPrivateCommentRepliesFields> =
  PostVideoPrivateCommentRepliesOptions<Fields>;

/**
 * Add a reply to a private comment
 * This method adds a reply to the specified private comment.
 */

export function usePostVideoPrivateCommentReplies<
  Fields extends PostVideoPrivateCommentRepliesFields,
>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PostVideoPrivateCommentRepliesResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePostVideoPrivateCommentRepliesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoPrivateCommentReplies({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoPrivateCommentReplies, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies',
    method: 'POST',
  });
}
